import '~/stylesheets/react-components/completed-projects-map.scss';
import mapboxgl from 'mapbox-gl';
import { observer, useLocalObservable } from 'mobx-react';
import { pauseForSpecs } from '~/javascripts/utils/testing';
import { useEffect, useLayoutEffect, useRef } from 'react';

let map, yellowMarker;

const Map = observer(
  ({
    accessToken,
    communityName,
    coordinates,
    countryCode,
    countryName,
    hideCoordinates,
    peopleServed,
    waitForAnimatedIntro,
  }) => {
    const mapEl = useRef(null);
    const offsetLatCenter = parseFloat(coordinates[1]) - 0.025;

    const localStore = useLocalObservable(() => ({
      loading: true,
      coordinates: coordinates,
      isFlying: false,
      isVisible: true,
      rotateMap: null,
    }));

    const getBrowserVisibilityProp = () => {
      if (typeof document.hidden !== 'undefined') {
        return 'visibilitychange';
      } else if (typeof document.msHidden !== 'undefined') {
        return 'msvisibilitychange';
      } else if (typeof document.webkitHidden !== 'undefined') {
        return 'webkitvisibilitychange';
      }
    };

    const getBrowserDocumentHiddenProp = () => {
      if (typeof document.hidden !== 'undefined') {
        return 'hidden';
      } else if (typeof document.msHidden !== 'undefined') {
        return 'msHidden';
      } else if (typeof document.webkitHidden !== 'undefined') {
        return 'webkitHidden';
      }
    };

    const getIsDocumentHidden = () => !document[getBrowserDocumentHiddenProp()];

    const onVisibilityChange = () => {
      localStore.isVisible = getIsDocumentHidden();
      if (localStore.isVisible) {
        localStore.rotateMap = setInterval(() => rotateMap(), 5);
      } else {
        clearInterval(localStore.rotateMap);
      }
    };

    useEffect(() => {
      const visibilityChange = getBrowserVisibilityProp();

      document.addEventListener(visibilityChange, onVisibilityChange, false);
      document.addEventListener(
        'AnimatedIntroComplete',
        addListenerAndFlyToLocation
      );

      return () => {
        document.removeEventListener(visibilityChange, onVisibilityChange);
        document.removeEventListener(
          'AnimatedIntroComplete',
          addListenerAndFlyToLocation
        );
      };
    }, [localStore.isVisible]);

    useLayoutEffect(() => {
      scrollToTopAndDisablePageScrolling();
      createConfiguredMap();
      attachMapEvents();

      const header = document.getElementById('vector-map');
      const heroText = document.getElementById('scroll-arrow');

      heroText.addEventListener('click', scrollToBoxyMapWithOffset);
      window.addEventListener('scroll', () => {
        fadeOutOnScroll(header);
      });

      return () => {
        document
          .getElementById('scroll-arrow')
          .removeEventListener('click', scrollToBoxyMapWithOffset);
      };

      pauseForSpecs(() => {
        map.on('idle', () => {
          localStore.loading = false;
        });
      });
    }, []);

    const scrollToTopAndDisablePageScrolling = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      document.getElementsByTagName('body')[0].style =
        'height: 100%; overflow: hidden';
    };

    const createConfiguredMap = () => {
      mapboxgl.accessToken = accessToken;

      map = new mapboxgl.Map({
        container: mapEl.current,
        style: 'mapbox://styles/mapbox/satellite-streets-v11',
        zoom: 4.35,
        center: localStore.coordinates,
        pitch: 50,
      });

      window.map = map;
      window.mapboxgl = mapboxgl;
    };

    const attachMapEvents = () => {
      yellowMarker = new mapboxgl.Marker({
        color: '#FFCA0A',
      })
        .setLngLat([localStore.coordinates[0], offsetLatCenter])
        .addTo(map);

      map.on('style.load', () => {
        map.scrollZoom.disable();
        map.boxZoom.disable();
        map.dragRotate.disable();
        map.dragPan.disable();
        map.keyboard.disable();
        map.doubleClickZoom.disable();
        map.touchZoomRotate.disable();

        if (window.railsEnv === 'test') {
          addMarkerAndTooltip();
        } else {
          if (!waitForAnimatedIntro) {
            flyToLocation();
          }
        }
      });

      map.once('moveend', () => {
        addMarkerAndTooltip();
      });

      map.on('click', 'points', () => {
        const viewportOffset = window.innerHeight * (30 / 100);
        const offset = 60 + viewportOffset;
        scrollToElementWithOffset(document.getElementById('boxy-map'), offset);
      });

      map.on('mouseenter', 'points', () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', 'points', () => {
        map.getCanvas().style.cursor = 'default';
      });
    };

    const addMarkerAndTooltip = () => {
      if (
        (localStore.isFlying && !map.isMoving()) ||
        window.railsEnv == 'test'
      ) {
        document.body.classList.add('open');
        document.body.style = 'overflow: visible';
        document.getElementById('scroll-arrow').classList.add('open');

        if (yellowMarker) {
          yellowMarker.remove();
          yellowMarker = null;
        }

        addTooltip();
        showStickyShareButton();

        localStore.rotateMap = setInterval(() => rotateMap(), 5);
        localStore.isFlying = false;
      }
    };

    const addListenerAndFlyToLocation = () => {
      map.once('moveend', () => {
        addMarkerAndTooltip();
      });

      flyToLocation();
    };

    const flyToLocation = () => {
      localStore.isFlying = true;
      map.flyTo({
        center: localStore.coordinates,
        zoom: 12,
        speed: 0.5,
        essential: true,
        pitch: 60,
      });
    };

    const addTooltip = () => {
      new mapboxgl.Popup({
        className: 'project-tooltip',
        closeButton: false,
        closeOnClick: false,
      })
        .setLngLat([coordinates[0], offsetLatCenter])
        .setHTML(tooltipContent())
        .addTo(map);
    };

    const tooltipContent = () => {
      const peopleServedText = peopleServed
        ? `${peopleServed} people`
        : communityName;

      return `<div class='p-1 text-center'>
              <span class='label-2xs tracking-wide block'>
                clean water for
              </span>
              <span class='body-s'>${peopleServedText}</span>
            </div>`;
    };

    const showStickyShareButton = () => {
      const stickyShareButton = document.getElementsByClassName(
        'sticky-share-button'
      )[0];
      stickyShareButton.style.opacity = 1;
    };

    const scrollToBoxyMapWithOffset = () => {
      const viewportOffset = window.innerHeight * (30 / 100);
      const offset = 60 + viewportOffset;
      scrollToElementWithOffset(document.getElementById('boxy-map'), offset);
    };

    const scrollToElementWithOffset = (element, offset) => {
      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      const interval = setInterval(() => {
        const vectorMap = document.getElementById('vector-map');
        fadeOutOnScroll(vectorMap);

        if (Math.abs(window.pageYOffset - offsetPosition) < 1) {
          vectorMap.style.opacity = '0';
          clearInterval(interval);
        }
      }, 10);
    };

    const fadeOutOnScroll = (element) => {
      if (!element) {
        return;
      }

      const distanceToTop =
        window.pageYOffset + (element.getBoundingClientRect().top - 400);
      const { scrollTop } = document.documentElement;

      let opacity = 1;

      if (scrollTop > distanceToTop) {
        opacity = 1 - scrollTop * 0.0031;
      } else {
        opacity = 0;
      }

      if (opacity >= 0) {
        element.style.opacity = opacity;
        document.getElementById('map').style.height = '100vh';
        document.querySelectorAll('.map-overlay')[0].style.height = '50%';
      } else {
        element.style.opacity = 0;
        document.getElementById('map').style.height = 'auto';
        document.querySelectorAll('.map-overlay')[0].style.height = '0';
      }
    };

    const rotateMap = () => {
      map.rotateTo((performance.now() / 1000) % 360, {
        duration: 500,
        around: [localStore.coordinates[0], offsetLatCenter],
        center: [localStore.coordinates[0], offsetLatCenter],
      });
    };

    return (
      <div
        className='theme-design-system-v1 completed-projects-page-map relative pb-8'
        style={{ minHeight: '200px' }}
      >
        <div className='top-0 p-0' id='vector-map'>
          <div className='fixed w-full left-0 m-0' id='map' ref={mapEl} />
          <div className='map-overlay' />
          <div className='scroll-down' id='scroll-arrow'>
            <img src='https://images.ctfassets.net/2w85ks0ylymt/7iYwtNccVMJ8XGPcGs1DUD/7d50e4b809d49d3de3e87d80209e40ca/arrow-2.png' />
          </div>
        </div>
        <div className='open sticky flex flex-col' id='hero-text'>
          <div className='py-8 text-center heading-xl'>
            <span className='cw-icon-jerrycan text-cw-brand-yellow-500' />
          </div>
          <div className='text-kazimir text-center'>
            <h1 className='heading-2xl'>{communityName}</h1>
            <div className='flex flex-row items-center justify-center w-full mt-4'>
              <div className='country flex flex-row items-center justify-center'>
                <span
                  className={`country-image bg-cover mr-3 inline-block icon-${countryCode}`}
                  style={{ width: '24px', height: '18px' }}
                />
                <span className='body-l'>{countryName}</span>
              </div>
              {!hideCoordinates && (
                <div className='coordinates flex flex-row items-center justify-center'>
                  <span
                    className='body-l mx-5 bg-cw-white'
                    style={{ width: '1px', height: '1.2rem' }}
                  />
                  <span className='icon cw-icon-location-outline text-md pr-2' />
                  <div className='no-underline text-cw-white font-normal body-l'>
                    <span className='body-l inline'>{`${localStore.coordinates[1].toFixed(
                      5
                    )}, ${localStore.coordinates[0].toFixed(5)}`}</span>
                  </div>
                  <a
                    className='gps-question no-underline body-xs ml-2'
                    href='#gps'
                  >
                    <span className='icon cw-icon-question-sign text-cw-white' />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ height: '80vh' }}>&nbsp;</div>
      </div>
    );
  }
);

export default Map;
